import { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CustomFormControl from './CustomFormControl';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import CustomHelperText from './CustomHelperText';
import { BEM_NAME_ELEMENTS } from 'pages/NewNonEmployee/_const';

/**
 * customized field prompting user for searchable input
 * note: this is for static data only and should not be used to query an API
 * @param {object[]} originalOptions list of options to select from containing `label` to display, `value` to store in state, and optional `group` to enable grouping by property
 * @param {string|ReactElement} label label to display
 * @param {string|ReactElement} helperText default on-screen assistance
 * @param {function} getValue optional overwrite function to populate the value of the autocomplete. Required if the value is an object. Default function checks if the value string is in the options object and returns it.
 * @param {function} isOptionEqualToValue optional overwrite function to pass to MUI Autocomplete to allow it to compare equality on object-based options.
 * @param {any} props properties to be passed to the autocomplete
 * @returns {ReactElement} autocomplete prompting for user input
 */
const StaticAutoComplete = ({
	originalOptions = [],
	label,
	helperText,
	getValue = (options, value) => options.find(o => o.value === value),
	isOptionEqualToValue = (option, value) => option.value === value,
	...props
}) => {
	const [field, meta, helpers] = useField(props);

	return (
		<CustomFormControl>
			<Autocomplete
				autoComplete
				value={
					field.value ? getValue(originalOptions, field.value) : null
				}
				clearIcon={null}
				disableClearable
				forcePopupIcon={true}
				getOptionLabel={option => option.label}
				groupBy={option => option.group}
				isOptionEqualToValue={isOptionEqualToValue}
				noOptionsText={'No results'}
				onChange={(_, newValue) => {
					if (newValue) {
						helpers.setValue(newValue.value);
					} else {
						helpers.setValue(null);
					}
				}}
				options={originalOptions}
				renderInput={params => (
					<TextField
						{...params}
						label={
							<>
								<RequiredIndicator />
								{label}
							</>
						}
						error={meta.touched && !!meta.error}
						onBlur={field.onBlur}
						onFocus={field.onFocus}
						{...props}
						inputProps={{
							...params.inputProps,
							id: `${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${props.name}`,
						}}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					const optionStr = option.label ? option.label : option;
					const matches = match(optionStr, inputValue, {
						insideWords: true,
						findAllOccurrences: true,
					});
					const parts = parse(optionStr, matches);

					return (
						<li {...props}>
							<div
								id={`${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_OPTIONS}--${optionStr}`}
							>
								{parts.map((part, index) => (
									<span
										key={index}
										style={{
											fontWeight: part.highlight
												? 700
												: 400,
										}}
									>
										{part.text}
									</span>
								))}
							</div>
						</li>
					);
				}}
				onBlur={field.onBlur}
				onFocus={field.onFocus}
			/>
			<CustomHelperText
				error={meta.error}
				touched={meta.touched}
				helperText={helperText}
			/>
		</CustomFormControl>
	);
};

StaticAutoComplete.propTypes = {
	label: PropTypes.string.isRequired,
	originalOptions: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
				.isRequired,
			group: PropTypes.string,
		})
	).isRequired,
	getValue: PropTypes.func,
	isOptionEqualToValue: PropTypes.func,
	helperText: PropTypes.string,
	name: PropTypes.string.isRequired,
};

export default StaticAutoComplete;
